import { Component, ElementRef, inject, OnInit } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import {
  AgentsService,
  ISimulationMessage,
} from '@app/services/agents.service';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { getApp } from '@angular/fire/app';
import { IAgent } from '@shared/interfaces';
import { MarkdownPipe } from '@shared/pipes/markdown.pipe';

@Component({
  selector: 'blu-agent-simulation',
  standalone: true,
  imports: [CommonModule, NgClass, FormsModule, MarkdownPipe],
  templateUrl: './simulation.component.html',
  styleUrl: './simulation.component.scss',
})
export class SimulationComponent implements OnInit {
  agentsService = inject(AgentsService);
  private elRef = inject(ElementRef);

  loadingResponse = false;
  agent$ = this.agentsService.agent$;

  ngOnInit(): void {
    setTimeout(() => this.scrollToBottom(), 0);
  }

  scrollToBottom(): void {
    this.elRef.nativeElement.scrollTop = this.elRef.nativeElement.scrollHeight;
  }

  async sendMessage() {
    const text = this.agentsService.messageContent;
    if (!text) return;
    this.loadingResponse = true;
    this.agentsService.messageContent = '';
    this.agentsService.exampleMessages.push({
      content: text,
      role: 'user',
      timestamp: getDateString(Date.now()),
    });
    const response = await this.generateResponse();
    this.agentsService.exampleMessages.push({
      content: response,
      role: 'assistant',
      timestamp: getDateString(Date.now()),
    });
    this.loadingResponse = false;
  }

  async generateResponse() {
    const functions = getFunctions(getApp(), 'southamerica-east1');
    const simulateConversation = httpsCallable(
      functions,
      'simulateConversation',
    );
    const agent = await firstValueFrom(this.agent$);
    const systemPrompt = buildSystemPrompt(agent);

    const brazilDateTime = getDateString(Date.now());
    const systemPromptWithDate = `Data e hora agora: ${brazilDateTime}\n\n${systemPrompt}`;

    const processedMessages = this.agentsService.exampleMessages
      .slice(-10)
      .map((msg) => ({
        role: msg.role,
        content: `[${msg.timestamp}] ${msg.content}`,
      }));

    const messages = [
      { role: 'system', content: systemPromptWithDate },
      ...processedMessages,
    ];
    const response = await simulateConversation({ messages });
    const data = response.data as string;
    return removeSquareBracketPrefix(data?.trim() || '')?.trim();
  }

  fromUser(message: ISimulationMessage) {
    return message.role === 'user';
  }
  fromAssistant(message: ISimulationMessage) {
    return message.role === 'assistant';
  }
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Sao_Paulo',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

function getDateString(date: number): string {
  return `${dateFormatter.format(date)}, ${timeFormatter.format(date)}`;
}

function removeSquareBracketPrefix(text: string): string {
  return text.replace(/^\[.*?\]/, '');
}

function buildSystemPrompt(agent: IAgent): string {
  let systemPrompt = agent.configuration.prompt;
  if (agent.configuration.faq && agent.configuration.faq.length > 0) {
    const faqSection =
      '\n\n## FAQ (Perguntas Frequentes)\n' +
      agent.configuration.faq
        .map(
          (item, index) =>
            `\nPergunta ${index + 1}: ${item.question}\nResposta ${index + 1}: ${
              item.answer
            }\n`,
        )
        .join('');

    systemPrompt += faqSection;
  }

  return systemPrompt;
}
